import { getTabConfig, getGenerateTabs } from "./tab-config";

import type { RouteRecordRaw } from "vue-router";
import type { Tab, MenuGroup, MenuList, View } from "@/types/webStyle";
import { IsViewGroup, ViewList } from "@/types/webStyle";
import type { UserStoreRef } from "@/stores/user.ts";

import { AuthType } from "@/auth";

// 路由级代码分割：使用动态 import 实现按需加载
const ProblemList = () => import("@/views/problem/ProblemList.vue");
const ProblemTagList = () => import("@/views/problem/ProblemTagList.vue");
const ProblemDetail = () => import("@/views/problem/ProblemDetail.vue");
const ProblemStatistics = () => import("@/views/problem/ProblemStatistics.vue");
const ProblemDailyList = () => import("@/views/problem/ProblemDailyList.vue");
const ProblemDailyEdit = () => import("@/views/problem/ProblemDailyEdit.vue");
const ProblemRecommend = () => import("@/views/problem/ProblemRecommend.vue");
const ProblemEdit = () => import("@/views/problem/ProblemEdit.vue");
const ProblemJudge = () => import("@/views/problem/ProblemJudge.vue");
const ProblemAiTestdata = () => import("@/views/problem/ProblemAiTestdata.vue");

const BotGameList = () => import("@/views/bot/BotGameList.vue");
const BotGame = () => import("@/views/bot/BotGame.vue");
const BotGameEdit = () => import("@/views/bot/BotGameEdit.vue");
const BotMatch = () => import("@/views/bot/BotMatch.vue");
const BotMatchList = () => import("@/views/bot/BotMatchList.vue");
const BotMatchCreate = () => import("@/views/bot/BotMatchCreate.vue");
const BotAgentList = () => import("@/views/bot/BotAgentList.vue");
const BotAgentEdit = () => import("@/views/bot/BotAgentEdit.vue");

const CollectionList = () => import("@/views/collection/CollectionList.vue");
const CollectionDetail = () => import("@/views/collection/CollectionDetail.vue");
const CollectionRank = () => import("@/views/collection/CollectionRank.vue");
const CollectionEdit = () => import("@/views/collection/CollectionEdit.vue");

const JudgeJobList = () => import("@/views/judge/JudgeJobList.vue");
const JudgeJobDetail = () => import("@/views/judge/JudgeJobDetail.vue");

const ContestList = () => import("@/views/contest/ContestList.vue");
const ContestDetail = () => import("@/views/contest/ContestDetail.vue");
const ContestRank = () => import("@/views/contest/ContestRank.vue");
const ContestEdit = () => import("@/views/contest/ContestEdit.vue");
const ContestReplayCreate = () => import("@/views/contest/ContestReplayCreate.vue");
const ContestStatistics = () => import("@/views/contest/ContestStatistics.vue");

const DiscussList = () => import("@/views/discuss/DiscussList.vue");
const DiscussDetail = () => import("@/views/discuss/DiscussDetail.vue");
const DiscussEdit = () => import("@/views/discuss/DiscussEdit.vue");

const RankUser = () => import("@/views/rank/RankUser.vue");
const UserForget = () => import("@/views/user/UserForget.vue");

const StudyView = () => import("@/views/extra/StudyView.vue");
const KAgentView = () => import("@/views/extra/KAgentView.vue");

// AI 教练主界面（会话列表 + 对话区）：浮窗只是快捷入口，两者共用同一套对话区组件与同一份 store 状态
const AiConversationView = () => import("@/views/ai/AiConversationView.vue");
// AI 教练「我的统计」：个人使用量 / token 成本 / 场景与常问题目的量化视角
const AiStatsView = () => import("@/views/ai/AiStatsView.vue");

const SystemStatus = () => import("@/views/system/SystemStatus.vue");

const ManageJudge = () => import("@/views/manage/ManageJudge.vue");
const ManageWeb = () => import("@/views/manage/ManageWeb.vue");
const ManageSystemMessage = () => import("@/views/manage/ManageSystemMessage.vue");
const ManageProblemEnrichment = () => import("@/views/manage/ManageProblemEnrichment.vue");
const ManageProblemEnrichmentDetail = () => import("@/views/manage/EnrichProblemDetailPage.vue");
const ManageTag = () => import("@/views/manage/ManageTag.vue");
const ManageAiCoachStats = () => import("@/views/manage/ManageAiCoachStats.vue");
const ManageAiProvider = () => import("@/views/manage/ManageAiProvider.vue");
const ManageAiSystemConfig = () => import("@/views/manage/ManageAiSystemConfig.vue");
const ManagePermission = () => import("@/views/permission/ManagePermission.vue");
const ContestRencently = () => import("@/views/contest/ContestRencently.vue");

const viewsConfig = [
  {
    name: "problem-list",
    tab: "problem",
    sidebar: "problem-list",
    path: "",
    title: "问题列表",
    icon: "file-1",
    component: ProblemList,
  },
  {
    // 必须排在 problem-detail 的 :problemKey+ 之前，否则 /problem/tags 会被它吃掉
    name: "problem-tags",
    tab: "problem",
    sidebar: "problem-tags",
    path: "tags",
    title: "问题标签",
    icon: "tag",
    component: ProblemTagList,
  },
  {
    name: "problem-detail",
    tab: "problem",
    sidebar: "problem-detail",
    path: ":problemKey+",
    title: "问题详情",
    icon: "article",
    component: ProblemDetail,
    disableSidebar: true,
  },
  {
    name: "problem-statistics",
    tab: "problem",
    sidebar: "problem-statistics",
    path: "statistics/:problemKey*",
    title: "提交统计",
    icon: "chart-bar",
    needLogin: false,
    disableSidebar: true,
    component: ProblemStatistics,
  },
  {
    name: "manage-problem-judge",
    tab: "problem",
    sidebar: "manage-problem-judge",
    disableSidebar: true,
    path: ":problemKey+/judge",
    title: "评测数据",
    icon: "data",
    component: ProblemJudge,
  },
  {
    name: "manage-problem-judge-ai",
    tab: "problem",
    sidebar: "manage-problem-judge-ai",
    disableSidebar: true,
    path: ":problemKey+/judge/ai/:jobId?",
    title: "AI 生成测试数据",
    icon: "lightbulb",
    auths: [AuthType.ManageProblem],
    component: ProblemAiTestdata,
  },
  {
    name: "problem-daily-list",
    tab: "problem",
    sidebar: "problem-daily-list",
    path: "daily",
    title: "每日一题",
    icon: "calendar-1",
    component: ProblemDailyList,
  },
  {
    name: "problem-daily-detail",
    tab: "problem",
    sidebar: "problem-daily-detail",
    path: "daily/:dailyId+",
    title: "问题详情",
    icon: "calendar-event",
    component: ProblemDetail,
    disableSidebar: true,
  },
  {
    name: "problem-recommend",
    tab: "problem",
    sidebar: "problem-recommend",
    path: "recommend/:problemKey*",
    title: "问题推荐",
    icon: "lightbulb",
    needLogin: true,
    component: ProblemRecommend,
  },
  {
    name: "problem-collection-list",
    tab: "problem",
    sidebar: "problem-collection-list",
    path: "collection",
    title: "题目集合",
    icon: "folder-1",
    disableTabPath: true,
    component: CollectionList,
  },
  {
    name: "collection-detail",
    tab: "collection-detail-tab",
    sidebar: "collection-detail",
    path: ":collectionId+",
    title: "题集详情",
    icon: "folder-details",
    component: CollectionDetail,
  },
  {
    name: "collection-rank",
    tab: "collection-detail-tab",
    sidebar: "collection-rank",
    path: ":collectionId+/rank",
    title: "题集排名",
    icon: "chart-line",
    component: CollectionRank,
  },
  {
    name: "collection-edit",
    tab: "collection-detail-tab",
    sidebar: "collection-edit",
    disableSidebar: true,
    needLogin: true,
    path: ":collectionId+/edit",
    title: "题集编辑",
    icon: "edit",
    component: CollectionEdit,
  },
  {
    name: "collection-create",
    tab: "collection-detail-tab",
    sidebar: "collection-create",
    needLogin: true,
    disableSidebar: true,
    path: "create",
    title: "新建题集",
    icon: "add",
    component: CollectionEdit,
  },

  {
    name: "judge-list",
    tab: "problem",
    sidebar: "judge-list",
    title: "评测列表",
    icon: "cloud",
    path: "judge",
    disableTabPath: true,
    component: JudgeJobList,
  },
  {
    name: "judge-detail",
    tab: "problem",
    sidebar: "judge-list",
    title: "评测详情",
    icon: "check-circle",
    path: "judge/:judgeId+",
    disableTabPath: true,
    component: JudgeJobDetail,
  },

  {
    name: "contest-list",
    tab: "contest",
    sidebar: "contest-list",
    path: "",
    title: "比赛列表",
    icon: "flag",
    component: ContestList,
  },
  {
    name: "contest-recent",
    tab: "contest",
    sidebar: "contest-recent",
    path: "recent",
    title: "近期比赛",
    icon: "time",
    component: ContestRencently,
  },
  {
    name: "contest-create",
    tab: "contest",
    sidebar: "contest-create",
    disableSidebar: true,
    needLogin: true,
    path: "create",
    title: "新建比赛",
    icon: "plus",
    component: ContestEdit,
  },
  {
    // 新建回放比赛：把「上传榜单生成回放」变成「新建一场仅用于回放的比赛」。
    // path 用两段静态路径（/contest/replay/create）：静态段的匹配优先级高于
    // contest-detail 的 `:contestId+` 通配，不会把 "replay/create" 当成比赛 ID；
    // 位置仍按惯例排在 contest-create 之后、contest-detail 之前。
    name: "contest-replay-create",
    tab: "contest",
    sidebar: "contest-replay-create",
    disableSidebar: true,
    needLogin: true,
    path: "replay/create",
    title: "新建回放比赛",
    icon: "time",
    component: ContestReplayCreate,
  },
  {
    name: "contest-edit",
    tab: "contest-detail-tab",
    sidebar: "contest-edit",
    disableSidebar: true,
    needLogin: true,
    path: ":contestId+/edit",
    title: "比赛编辑",
    icon: "edit",
    component: ContestEdit,
  },
  {
    name: "contest-detail",
    tab: "contest-detail-tab",
    sidebar: "contest-detail",
    path: ":contestId+",
    title: "比赛详情",
    icon: "flag-1",
    component: ContestDetail,
  },
  {
    name: "contest-problem-detail",
    tab: "contest-detail-tab",
    sidebar: "contest-problem-detail",
    path: ":contestId+/problem/:problemIndex+",
    title: "问题详情",
    icon: "file-code",
    disableSidebar: true,
    component: ProblemDetail,
  },
  {
    name: "bot-game-list",
    tab: "bot",
    sidebar: "bot-game-list",
    path: "",
    title: "游戏列表",
    icon: "flag",
    component: BotGameList,
  },
  {
    name: "bot-game",
    tab: "bot",
    sidebar: "bot-game-list",
    path: ":gameKey+",
    title: "游戏详情",
    icon: "play",
    component: BotGame,
  },
  {
    name: "bot-match-create",
    tab: "bot",
    sidebar: "bot-game-list",
    path: "match/create",
    title: "新建游戏",
    icon: "play",
    component: BotMatchCreate,
  },
  {
    name: "bot-match-list",
    tab: "bot",
    sidebar: "bot-match-list",
    path: "match",
    title: "对局列表",
    icon: "camera",
    component: BotMatchList,
  },
  {
    name: "bot-match",
    tab: "bot",
    sidebar: "bot-match-list",
    path: ":gameKey+/match/:matchId+",
    title: "对局详情",
    icon: "play",
    component: BotMatch,
  },
  {
    name: "bot-agent-list",
    tab: "bot",
    sidebar: "bot-agent-list",
    path: "agent",
    title: "选手列表",
    icon: "user",
    component: BotAgentList,
  },
  {
    name: "bot-agent-create",
    tab: "bot",
    sidebar: "bot-agent-list",
    path: "agent/create",
    title: "新建选手",
    icon: "add",
    needLogin: true,
    component: BotAgentEdit,
  },
  {
    name: "bot-agent-edit",
    tab: "bot",
    sidebar: "bot-agent-list",
    path: "agent/edit/:agentId+",
    title: "编辑选手",
    icon: "edit",
    needLogin: true,
    component: BotAgentEdit,
  },
  {
    name: "bot-about",
    tab: "bot",
    sidebar: "bot-about",
    path: "about",
    title: "对战说明",
    icon: "info-circle",
    props: { discussId: "171" },
    component: DiscussDetail,
  },

  {
    name: "manage-bot-game",
    tab: "bot",
    sidebar: "manage-bot-game",
    disableSidebar: true,
    path: "game/:gameKey+",
    title: "游戏编辑",
    icon: "edit",
    component: BotGameEdit,
    // 游戏编辑请求的 i-manage-problem 与后端 BotService.CheckGameEditAuth 一致
    // （原先写的 i-manage-bot-game 后端并不存在）
    auths: [AuthType.ManageProblem],
  },
  {
    name: "contest-judge",
    tab: "contest-detail-tab",
    sidebar: "contest-judge",
    path: ":contestId+/judge",
    title: "评测列表",
    icon: "cloud",
    component: JudgeJobList,
  },
  {
    name: "contest-judge-detail",
    tab: "contest-detail-tab",
    sidebar: "contest-judge-detail",
    path: ":contestId+/judge/:judgeId+",
    title: "评测详情",
    icon: "check-circle",
    component: JudgeJobDetail,
    disableSidebar: true,
  },
  {
    name: "contest-rank",
    tab: "contest-detail-tab",
    sidebar: "contest-rank",
    path: ":contestId+/rank",
    title: "比赛排名",
    icon: "chart-bar",
    component: ContestRank,
  },
  {
    name: "contest-discuss",
    tab: "contest-detail-tab",
    sidebar: "contest-discuss",
    path: ":contestId+/discuss",
    title: "比赛讨论",
    icon: "chat",
    component: DiscussList,
  },
  {
    name: "contest-discuss-detail",
    tab: "contest-detail-tab",
    sidebar: "contest-discuss",
    path: ":contestId+/discuss/:discussId+",
    title: "讨论详情",
    icon: "chat-bubble",
    component: DiscussDetail,
  },
  {
    name: "contest-statistics",
    tab: "contest-detail-tab",
    sidebar: "contest-statistics",
    path: ":contestId+/statistics",
    title: "提交统计",
    icon: "chart-line",
    component: ContestStatistics,
  },

  {
    name: "discuss-list",
    tab: "discuss",
    sidebar: "discuss-list",
    path: "",
    title: "讨论列表",
    icon: "chat",
    component: DiscussList,
  },
  {
    name: "discuss-create",
    tab: "discuss",
    sidebar: "discuss-create",
    disableSidebar: true,
    needLogin: true,
    path: "create",
    title: "新建讨论",
    icon: "add-circle",
    component: DiscussEdit,
  },
  {
    name: "discuss-edit",
    tab: "discuss-detail-tab",
    sidebar: "discuss-edit",
    disableSidebar: true,
    needLogin: true,
    path: ":discussId+/edit",
    title: "编辑讨论",
    icon: "edit",
    component: DiscussEdit,
  },
  {
    name: "discuss-list-problem",
    tab: "discuss",
    sidebar: "discuss-list-problem",
    path: "problem",
    title: "题目讨论",
    icon: "file",
    component: DiscussList,
  },
  {
    name: "discuss-detail",
    tab: "discuss",
    sidebar: "discuss-list",
    path: ":discussId+",
    title: "讨论详情",
    icon: "chat-bubble",
    component: DiscussDetail,
  },

  {
    name: "rank-ac-all",
    tab: "rank",
    sidebar: "rank-ac-all",
    path: "ac",
    title: "AC排名",
    icon: "chart-bar",
    props: {
      type: "all",
    },
    component: RankUser,
  },
  {
    name: "rank-ac-problem",
    tab: "rank",
    sidebar: "rank-ac-problem",
    path: "ac/problem",
    title: "题量排名",
    icon: "chart-pie",
    props: {
      type: "problem",
    },
    component: RankUser,
  },
  {
    name: "rank-ac-problem-today",
    tab: "rank",
    sidebar: "rank-ac-problem-today",
    path: "ac/problem/today",
    title: "今日题量",
    icon: "brightness",
    props: {
      type: "problem-today",
    },
    component: RankUser,
  },
  {
    name: "rank-ac-problem-day7",
    tab: "rank",
    sidebar: "rank-ac-problem-day7",
    path: "ac/problem/day7",
    title: "7日题量",
    icon: "calendar",
    props: {
      type: "problem-day7",
    },
    component: RankUser,
  },
  {
    name: "rank-ac-problem-day30",
    tab: "rank",
    sidebar: "rank-ac-problem-day30",
    path: "ac/problem/day30",
    title: "30日题量",
    icon: "calendar-2",
    props: {
      type: "problem-day30",
    },
    component: RankUser,
  },
  {
    name: "rank-ac-problem-year",
    tab: "rank",
    sidebar: "rank-ac-problem-year",
    path: "ac/problem/year",
    title: "1年题量",
    icon: "chart-3d",
    props: {
      type: "problem-year",
    },
    component: RankUser,
  },

  {
    name: "study-view",
    tab: "study",
    sidebar: "study-view",
    path: "study",
    title: "课程方案",
    icon: "book-open",
    disableTabPath: true,
    component: StudyView,
  },
  {
    name: "study-agent",
    tab: "study",
    sidebar: "study-agent",
    path: "aagent",
    title: "Kruskal",
    icon: "chart-bubble",
    disableTabPath: true,
    component: KAgentView,
  },

  // AI 教练：主界面（侧边栏「AI 教练 → 我的对话」）
  // 两条路由指向同一个主界面（左列表 + 右对话），地址形态分「未选中」与「选中某条会话」：
  // /ai/conversation 未选中会话（新建对话也回落到这里），/ai/conversation/:id 是选中会话的规范地址
  // （点左侧条目、浮窗「在主界面打开」都写到这里）；旧版的 /ai/conversation?id= 仍可用，进入后会把地址收敛成 :id。
  // 两条路由共用 viewKey，App.vue 据此把它们当同一页面渲染：同页切换地址不重建组件、不闪加载条。
  // 只读语义由「非本人会话不可见」（服务端 100096）保证，不必再多一个页面。
  // 详情路由名沿用 ai-conversation-detail，避免改动既有按 name 跳转的调用方。
  {
    name: "ai-conversation",
    tab: "ai",
    sidebar: "ai-conversation",
    path: "conversation",
    title: "我的对话",
    icon: "chat",
    needLogin: true,
    viewKey: "ai-conversation",
    component: AiConversationView,
  },
  {
    name: "ai-conversation-detail",
    tab: "ai",
    sidebar: "ai-conversation",
    path: "conversation/:id+",
    title: "我的对话",
    icon: "chat-bubble",
    needLogin: true,
    viewKey: "ai-conversation",
    component: AiConversationView,
  },
  {
    // AI 教练「我的统计」：个人使用量 / token 成本 / 场景分布 / 常问题目，
    // 只读聚合（后端 /ai/stats 仅本人数据，登录即可），作为学习优化方向的量化依据
    name: "ai-stats",
    tab: "ai",
    sidebar: "ai-stats",
    path: "stats",
    title: "我的统计",
    icon: "chart-bar",
    needLogin: true,
    component: AiStatsView,
  },

  {
    name: "system-status",
    tab: "system",
    sidebar: "system-status",
    path: "",
    title: "运行状态",
    icon: "setting",
    component: SystemStatus,
  },

  {
    name: "system-about",
    tab: "system",
    sidebar: "system-about",
    path: "about",
    title: "关于",
    icon: "info-circle",
    props: { discussId: "93" },
    component: DiscussDetail,
  },

  {
    name: "manage-problem-create",
    tab: "manage",
    sidebar: "manage-problem-create",
    disableSidebar: true,
    path: "problem",
    title: "新建问题",
    icon: "file-add",
    component: ProblemEdit,
    auths: [AuthType.ManageProblem],
  },
  {
    name: "manage-problem",
    tab: "manage",
    sidebar: "manage-problem",
    disableSidebar: true,
    path: "problem/:problemKey+",
    title: "问题编辑",
    icon: "edit",
    component: ProblemEdit,
    auths: [AuthType.ManageProblem],
  },
  {
    name: "manage-problem-daily-create",
    tab: "manage",
    sidebar: "manage-problem-daily-create",
    disableSidebar: true,
    path: "problem/daily",
    title: "新建每日一题",
    icon: "add",
    component: ProblemDailyEdit,
    auths: [AuthType.ManageProblemDaily],
  },
  {
    name: "manage-problem-daily",
    tab: "manage",
    sidebar: "manage-problem-daily",
    disableSidebar: true,
    path: "problem/daily/:dailyId+",
    title: "编辑每日一题",
    icon: "calendar-edit",
    component: ProblemDailyEdit,
    auths: [AuthType.ManageProblemDaily],
  },
  {
    name: "manage-web",
    tab: "manage",
    path: "web",
    sidebar: "manage-web",
    title: "站点管理",
    icon: "setting",
    auths: [AuthType.ManageWeb],
    component: ManageWeb,
  },
  {
    name: "manage-judge",
    tab: "manage",
    path: "judge",
    sidebar: "manage-judge",
    title: "评测管理",
    icon: "cpu",
    auths: ["i-manage-judge"],
    component: ManageJudge,
  },
  {
    name: "manage-system-message",
    tab: "manage",
    path: "system-message",
    sidebar: "manage-system-message",
    title: "系统消息",
    icon: "notification",
    auths: [AuthType.ManageWeb],
    component: ManageSystemMessage,
  },
  {
    name: "manage-permission",
    tab: "manage",
    path: "permission",
    sidebar: "manage-permission",
    title: "权限管理",
    icon: "lock-on",
    // 只认 i-manage-permission，与后端 PermissionController 的门禁一致。
    // 刻意不复用 ManageWeb：站点管理（公告/友链/Banner）与「授予他人权限」的风险
    // 差着量级，前者只影响展示，后者能改变谁能做什么。
    //
    // 「管理」tab 自身不设门禁，入口是否显示由本页（及其他管理子页）的 auths 推导出来，
    // 因此一个只有 i-manage-permission 的管理员照样能进到这个页面。
    auths: [AuthType.ManagePermission],
    component: ManagePermission,
  },
  {
    name: "manage-problem-enrichment",
    tab: "manage",
    path: "problem-enrichment",
    sidebar: "manage-problem-enrichment",
    title: "AI 审阅",
    // tdesign-icons@0.4.1 并未提供 brain/sparkle 一类 AI 图标（写 brain 会渲染成空白）；
    // AI 审阅本质是为题目建立摘要/标签/难度/质量等结构化元数据目录，故用 catalog 图标
    icon: "catalog",
    // AI 审阅产物是题目的元数据（摘要/标签/难度/质量），门禁与题目管理一致
    auths: [AuthType.ManageProblem],
    component: ManageProblemEnrichment,
  },
  {
    // 标签管理：词表治理从「AI 审阅」后端里拆出来独立成页。
    // AI 审阅看的是流水线（谁跑到哪一步、失败在哪），标签管理看的是词表本身
    // （一个概念有几种叫法、挂了多少题、展示名由人工还是 AI 定），两件事的受众与
    // 操作节奏都不同，挤在一个 tab 里两个用途都做不深。
    name: "manage-tag",
    tab: "manage",
    path: "tag",
    sidebar: "manage-tag",
    title: "标签管理",
    // tdesign-icons@0.4.1 的 manifest 里确有 tag 这一项（stem "tag" → Tag 组件），
    // 可以直接用；万一将来升级图标库把它去掉，可退到 discount/filter/layers
    icon: "tag",
    // 词表是全站标签的公共底座，改动影响所有题目的检索与展示，门禁与题目管理一致
    auths: [AuthType.ManageProblem],
    component: ManageTag,
  },
  {
    // AI 教练全站统计（只读聚合，不含任何用户对话内容），与 AI 审阅后台同门禁：
    // AI 能力的管理视图统一归题目管理员（i-manage-problem），不新增权限点
    name: "manage-ai-coach-stats",
    tab: "manage",
    path: "ai-coach/stats",
    sidebar: "manage-ai-coach-stats",
    title: "教练统计",
    icon: "chart-pie",
    auths: [AuthType.ManageProblem],
    component: ManageAiCoachStats,
  },
  {
    // AI 厂商白名单管理：用户侧 BYOK 的基础设施（接入地址/默认模型/停用删除）。
    // 门禁用独立的 i-manage-ai：改动厂商直接影响全体用户的 AI 功能，风险面与题目管理不同
    name: "manage-ai-provider",
    tab: "manage",
    path: "ai-provider",
    sidebar: "manage-ai-provider",
    title: "AI 厂商",
    // tdesign-icons@0.4.1 没有 brain/sparkle 一类 AI 图标；厂商表本质是接入配置，用 api 图标
    icon: "api",
    auths: [AuthType.ManageAI],
    component: ManageAiProvider,
  },
  {
    // 系统 AI 配置：站方系统级的大模型凭据与各能力模型名（替代后端 yaml 的 ai 配置段）。
    // 与「AI 厂商」（用户侧 BYOK 白名单）同门禁 i-manage-ai：两者都是 AI 接入的凭据面，
    // 改错一样会影响全体用户的 AI 功能
    name: "manage-ai-system-config",
    tab: "manage",
    path: "ai-system-config",
    sidebar: "manage-ai-system-config",
    title: "系统 AI 配置",
    icon: "setting-1",
    auths: [AuthType.ManageAI],
    component: ManageAiSystemConfig,
  },
  {
    // AI 审阅产出详情独立页（全宽展示，可刷新/收藏）；「查看产出」统一跳转到这里，
    // path 用题目 key（可读 URL），与题目详情页 /problem/:problemKey+ 同风格。
    // sidebar 对齐到「AI 审阅」而非自身 name：侧边栏菜单只收录 sidebar === name 的页面，
    // 若对齐自身，详情页会成为可直接点击的菜单项，但没有 problemKey 参数只能看到空态；
    // 对齐父菜单后，详情页保留侧边栏并高亮「AI 审阅」，菜单里不再出现「审阅详情」入口。
    name: "manage-problem-enrichment-detail",
    tab: "manage",
    path: "problem-enrichment/detail/:problemKey?",
    sidebar: "manage-problem-enrichment",
    title: "审阅详情",
    icon: "article",
    auths: [AuthType.ManageProblem],
    component: ManageProblemEnrichmentDetail,
  },

  {
    name: "login-forget",
    tab: "login",
    path: "forget",
    title: "忘记密码",
    component: UserForget,
  },
] as ViewList;

export const parseTabMenu = (
  list: MenuList,
  toSidebar: string,
  tabName: string,
  view: View,
  userStore: UserStoreRef
) => {
  if (view.tab != tabName) {
    return;
  }
  if (view.disableSidebar && toSidebar != view.sidebar) {
    return;
  }
  if (view.sidebar == null || view.sidebar != view.name) {
    return;
  }
  // 权限不足的项不进菜单。路由守卫已经会拦（router/index.ts 同时校验 tabAuths 与
  // view.auths），但把一个点进去必然 403 的入口显示出来是纯粹的误导——
  // 尤其在凭据或权限刚被调整之后，用户会以为是页面坏了。
  if (!userStore.hasAllAuths(view.auths ?? [])) {
    return;
  }
  list.push({
    name: view.name,
    title: view.title,
    icon: view.icon,
    to: { name: view.name },
  });
};

export const getTabSubMenus = (toSidebar: string, tabName: string, userStore: UserStoreRef) => {
  const menus = [] as MenuList;
  viewsConfig.forEach((view) => {
    if (IsViewGroup(view)) {
      const views = [] as MenuList;
      view.children.forEach((childView) => {
        parseTabMenu(views, toSidebar, tabName, childView, userStore);
      });
      if (views.length > 0) {
        menus.push({
          title: view.title,
          children: views,
        } as MenuGroup);
      }
    } else {
      parseTabMenu(menus, toSidebar, tabName, view, userStore);
    }
  });
  return menus;
};

// 获取所有 tab 的子菜单树（用于移动端导航抽屉）
// 返回每个 tab 对应的子菜单列表，当前 tab 默认展开
export const getAllTabMenus = (userStore: UserStoreRef) => {
  const tabs = getVisibleTabs(userStore);
  return tabs.map((tab) => {
    // 用 tab.redirect 对应 view 的 sidebar 作为 toSidebar
    // 这样获取的子菜单和桌面端进入该 tab 后看到的侧边栏一致
    const redirectView = viewsConfig.find(
      (v) => !IsViewGroup(v) && (v as View).name === tab.redirect
    ) as View | undefined;
    const toSidebar = redirectView?.sidebar || "";
    const subMenus = getTabSubMenus(toSidebar, tab.name, userStore);
    return {
      tab,
      subMenus,
    };
  });
};

// 该 tab 的子菜单（桌面端顶栏与移动端抽屉共用同一套推导）
const tabSubMenusOf = (tab: Tab, userStore: UserStoreRef) => {
  const redirectView = viewsConfig.find(
    (v) => !IsViewGroup(v) && (v as View).name === tab.redirect
  ) as View | undefined;
  const toSidebar = redirectView?.sidebar || "";
  return getTabSubMenus(toSidebar, tab.name, userStore);
};

// resolveTabLanding 决定进入某个 tab 时落到哪个子页。
//
// 不能直接跟着 tab.redirect 走：那是写给「权限齐全的人」的默认落点。当用户没有那个子页的
// 权限时——例如只有 i-manage-permission 的管理员，而「管理」的默认落点是 manage-web——
// 跟着 redirect 会把他送到 403。**入口看得见、点进去却报错**是最让人困惑的一种状态，
// 比不显示入口更糟。
//
// 规则：默认落点可用就用它（保持大多数人的既有习惯），否则取该 tab 下第一个有权限的子页。
// 候选范围与侧边栏一致（`sidebar === name` 的那些），保证落点一定是侧边栏里点得到的那一项。
export const resolveTabLanding = (
  tabName: string,
  preferred: string | undefined,
  userStore: UserStoreRef
): string | undefined => {
  const canOpen = (view: View) => userStore.hasAllAuths(view.auths ?? []);
  const isSidebarItem = (view: View) => view.sidebar != null && view.sidebar === view.name;

  const preferredView = viewsConfig.find(
    (v) => !IsViewGroup(v) && (v as View).name === preferred
  ) as View | undefined;
  if (preferredView && canOpen(preferredView)) {
    return preferredView.name;
  }

  const fallback = viewsConfig.find(
    (v) => !IsViewGroup(v) && (v as View).tab === tabName && isSidebarItem(v) && canOpen(v)
  ) as View | undefined;
  return fallback?.name;
};

// getVisibleTabs 在 getGenerateTabs 的基础上，再去掉「一个子页都进不去」的容器 tab。
//
// 为什么需要这一步：容器 tab（如「管理」）刻意不设自己的 auths——它的门禁应当由子页推导，
// 否则「只有题目管理权限的助教」会因为容器门禁不满足而整个 tab 消失，他手里的权限也就
// 没有入口可用。但这样一来，一个没有任何管理权限的普通用户会看到一个点开空无一物的
// 「管理」tab，比不显示更让人困惑。
//
// 推导规则：showSidebar 的 tab 必须有至少一个可见子页才显示；不显示侧边栏的 tab
// （如各详情页 tab）没有子菜单可推导，保持原样由自身的 auths 决定。
// 这样新增一个管理子页时不需要回来维护任何清单。
export const getVisibleTabs = (userStore: UserStoreRef) => {
  return getGenerateTabs(userStore).filter((tab) => {
    if (!tab.showSidebar) {
      return true;
    }
    return tabSubMenusOf(tab, userStore).length > 0;
  });
};

export const parseViewRouter = (routers: RouteRecordRaw[], view: View) => {
  const tabConfig = getTabConfig(view.tab) as Tab;

  const router: RouteRecordRaw = {
    name: view.name,
    path: "/" + view.path,
    meta: {
      title: view.title + " - " + tabConfig.title,
      tab: view.tab,
      sidebar: view.sidebar,
      showSidebar: tabConfig.showSidebar,
      needLogin: tabConfig.needLogin || view.needLogin,
      tabAuths: tabConfig.auths,
      auths: view.auths,
      viewKey: view.viewKey,
    },
    props: view.props,
    component: view.component,
  };

  if (!tabConfig.disablePath && !view.disableTabPath) {
    router.path = tabConfig.path + router.path;
  }

  routers.push(router);
};

export const makeViewRouter = (routers: RouteRecordRaw[]) => {
  viewsConfig.forEach((view) => {
    if (IsViewGroup(view)) {
      view.children.forEach((childView) => {
        parseViewRouter(routers, childView);
      });
    } else {
      parseViewRouter(routers, view);
    }
  });
};
